import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ColorModeIconDropdown from "../shared-theme/ColorModeIconDropdown";
import { SynergiIcon } from "../sign-in/components/CustomIcons";
import AppTheme from "../shared-theme/AppTheme";
import { useAuth } from "../../hooks/useAuth";
import { Menu } from "@mui/material";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: "8px 12px",
}));

export default function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const [definitionsAnchorEl, setDefinitionsAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const { loggedIn, role, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path: string) => location.pathname === path;
  const isDefinitionsMenuOpen = Boolean(definitionsAnchorEl);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const handleLogout = () => {
    logout();
    navigate("/sign-in");
  };

  const handleDefinitionsClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDefinitionsAnchorEl(event.currentTarget);
  };

  const handleDefinitionsClose = () => {
    setDefinitionsAnchorEl(null);
  };

  return (
    <AppTheme>
      <AppBar
        position="static"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: "transparent",
          backgroundImage: "none",
          mt: "calc(var(--template-frame-height, 0px) + 28px)",
        }}
      >
        <Container maxWidth="xl">
          <StyledToolbar variant="dense" disableGutters>
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 0,
              }}
            >
              <Button component={Link}
                    to={role === "Admin" ? "/dashboard" : "/create-cv"}>
              <SynergiIcon />
              </Button>
              {loggedIn && (
                <Box sx={{ display: { xs: "none", md: "flex" } }}>
                  {role === "Admin" && (
                    <>
                      <Button
                        component={Link}
                        to="/dashboard"
                        variant="text"
                        size="small"
                        sx={(theme) => ({
                          backgroundColor: isActive("/dashboard")
                            ? theme.palette.mode === "dark"
                              ? theme.palette.grey[700]
                              : theme.palette.grey[100]
                            : "transparent",
                        })}
                      >
                        Dashboard
                      </Button>
                      <Button
                        component={Link}
                        to="/user-management"
                        variant="text"
                        size="small"
                        sx={(theme) => ({
                          backgroundColor: isActive("/user-management")
                            ? theme.palette.mode === "dark"
                              ? theme.palette.grey[700]
                              : theme.palette.grey[100]
                            : "transparent",
                          })}
                      >
                        User Management
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleDefinitionsClick}
                        sx={(theme) => ({
                          backgroundColor: [
                            "/definitions/positions",
                            "/definitions/trainings",
                            "/definitions/skills",
                          ].includes(location.pathname)
                            ? theme.palette.mode === "dark"
                              ? theme.palette.grey[700]
                              : theme.palette.grey[100]
                            : "transparent",
                          })}
                      >
                        Definitions
                      </Button>
                      <Menu
                        anchorEl={definitionsAnchorEl}
                        open={isDefinitionsMenuOpen}
                        onClose={handleDefinitionsClose}
                      >
                        <MenuItem
                          component={Link}
                          to="/definitions/positions"
                          onClick={handleDefinitionsClose}
                        >
                          Positions
                        </MenuItem>
                        <MenuItem
                          component={Link}
                          to="/definitions/trainings"
                          onClick={handleDefinitionsClose}
                        >
                          Training/Certifications
                        </MenuItem>
                        <MenuItem
                          component={Link}
                          to="/definitions/skills"
                          onClick={handleDefinitionsClose}
                        >
                          Skills
                        </MenuItem>
                      </Menu>
                    </>
                  )}

<Button
                  component={Link}
                  to="/create-cv"
                  variant="text"
                  size="small"
                  sx={(theme) => ({
                    backgroundColor: isActive("/create-cv")
                      ? theme.palette.mode === "dark"
                        ? theme.palette.grey[700]
                        : theme.palette.grey[100]
                      : "transparent",
                  })}
                >
                  CV-Creator
                </Button>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                marginLeft: "10px",
                display: { xs: "none", md: "flex" },
                gap: 1,
                alignItems: "center",
              }}
            >
              {loggedIn ? (
                <Button
                  color="error"
                  variant="outlined"
                  fullWidth
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              ) : (
                <Button
                  component={Link}
                  to="/sign-in"
                  variant="outlined"
                  fullWidth
                >
                  Sign in
                </Button>
              )}
              <ColorModeIconDropdown />
            </Box>
            <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1 }}>
              <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="top"
                open={open}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    top: "var(--template-frame-height, 0px)",
                  },
                }}
              >
                <Box sx={{ p: 2, backgroundColor: "background.default" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <IconButton onClick={toggleDrawer(false)}>
                      <CloseRoundedIcon />
                    </IconButton>
                  </Box>
                  <SynergiIcon />
                  {loggedIn && (
                    <>
                      <Link to="/">
                        <MenuItem selected={isActive("/")}>CV-CREATOR</MenuItem>
                      </Link>
                      {role === "Admin" && (
                        <>
                          <Link to="/dashboard">
                            <MenuItem selected={isActive("/dashboard")}>
                              Dashboard
                            </MenuItem>
                          </Link>
                          <Link to="/user-management">
                            <MenuItem selected={isActive("/user-management")}>
                              User Management
                            </MenuItem>
                          </Link>
                          <Link to="/definitions/positions">
                            <MenuItem
                              selected={isActive("/definitions/positions")}
                            >
                              Positions
                            </MenuItem>
                          </Link>
                          <Link to="/definitions/trainings">
                            <MenuItem
                              selected={isActive("/definitions/trainings")}
                            >
                              Training/Certifications
                            </MenuItem>
                          </Link>

                          <Link to="/definitions/skills">
                            <MenuItem
                              selected={isActive("/definitions/skills")}
                            >
                              Skills
                            </MenuItem>
                          </Link>
                        </>
                      )}
                      <MenuItem>
                        <ColorModeIconDropdown />
                      </MenuItem>
                    </>
                  )}
                  <Divider sx={{ my: 3 }} />
                  <MenuItem>
                    {loggedIn ? (
                      <Button
                        color="error"
                        variant="outlined"
                        fullWidth
                        onClick={handleLogout}
                      >
                        Logout
                      </Button>
                    ) : (
                      <Button
                        component={Link}
                        to="/sign-in"
                        variant="outlined"
                        fullWidth
                      >
                        Sign in
                      </Button>
                    )}
                  </MenuItem>
                </Box>
              </Drawer>
            </Box>
          </StyledToolbar>
        </Container>
      </AppBar>
    </AppTheme>
  );
}
